import app from '../../forum/app';
import Modal, { IInternalModalAttrs } from '../../common/components/Modal';
import LogInModal from './LogInModal';
import Button from '../../common/components/Button';
import LogInButtons from './LogInButtons';
import extractText from '../../common/utils/extractText';
import ItemList from '../../common/utils/ItemList';
import Stream from '../../common/utils/Stream';
import type Mithril from 'mithril';
import CommentUtil from '../../common/dianliang/commentUtil';
import RequestError from '../../common/utils/RequestError';

export interface ISignupModalAttrs extends IInternalModalAttrs {
  username?: string;
  email?: string;
  password?: string;
  code?: string;
  token?: string;
  provided?: string[];
  fromFlorum?: boolean;
}

export type SignupBody = {
  username: string;
  email: string;
  code: string;
} & ({ token: string } | { password: string });

export default class SignUpModal<CustomAttrs extends ISignupModalAttrs = ISignupModalAttrs> extends Modal<CustomAttrs> {
  /**
   * The value of the username input.
   */
  username!: Stream<string>;

  /**
   * The value of the email input.
   */
  email!: Stream<string>;

  /**
   * The value of the password input.
   */
  password!: Stream<string>;

  code!: Stream<string>;

  fromFlorum!: Stream<boolean>;
  isSending!: Stream<boolean>;
  secondsLeft!: Stream<number>;
  oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
    super.oninit(vnode);

    this.username = Stream(this.attrs.username || '');
    this.email = Stream(this.attrs.email || '');
    this.password = Stream(this.attrs.password || '');
    this.code = Stream(this.attrs.password || '');
    this.isSending = Stream(false);
    this.secondsLeft = Stream(60);
  }

  className() {
    return 'Modal--small SignUpModal';
  }

  title() {
    return app.translator.trans('core.forum.sign_up.title');
  }

  content() {
    return [<div className="Modal-body">{this.body()}</div>, <div className="Modal-footer">{this.footer()}</div>];
  }

  isProvided(field: string): boolean {
    return this.attrs.provided?.includes(field) ?? false;
  }

  body() {
    let pkgLogo = app.data.pkgLogo;
    // 将字符串映射转换为 JavaScript 对象
    const obj = JSON.parse(pkgLogo.pkgLogo);
    // 创建 Map 对象并初始化
    const map = new Map(Object.entries(obj));
    // 获取特定键的值
    let localePkgLogo = map.get(app.data.locale);
    return [
      !this.attrs.token && <LogInButtons />,
      <img className="Login-title-img" src={localePkgLogo}></img>,
      <div className="Form Form--centered">{this.fields().toArray()}</div>,
    ];
  }

  fields() {
    const items = new ItemList();

    const usernameLabel = extractText(app.translator.trans('core.forum.sign_up.username_placeholder'));
    const emailLabel = extractText(app.translator.trans('core.forum.sign_up.email_placeholder'));
    const passwordLabel = extractText(app.translator.trans('core.forum.sign_up.password_placeholder'));
    const codeLabel = extractText(app.translator.trans('dl_sendCode.forum.code_placeholder'));

    items.add(
      'username',
      <div className="Form-group">
        <input
          className="FormControl"
          name="username"
          type="text"
          placeholder={usernameLabel}
          aria-label={usernameLabel}
          bidi={this.username}
          disabled={this.loading || this.isProvided('username')}
        />
      </div>,
      30
    );

    items.add(
      'email',
      <div className="Form-group">
        <input
          className="FormControl"
          name="email"
          type="email"
          placeholder={emailLabel}
          aria-label={emailLabel}
          bidi={this.email}
          disabled={this.loading || this.isProvided('email')}
        />
      </div>,
      20
    );

    items.add(
      'code',
      <div className="Form-group input-code">
        <input
          className="FormControl"
          name="code"
          placeholder={codeLabel}
          aria-label={codeLabel}
          bidi={this.code}
          disabled={this.loading || this.isProvided('code')}
        />
        <button className="Button Button--icon CommandButton" type="button" onclick={this.sendCode.bind(this)} disabled={this.isSending()}>
          <span>{this.isSending() ? String(this.secondsLeft()) + app.translator.trans('dl_sendCode.forum.send_code_time') : app.translator.trans('dl_sendCode.forum.send_code')}</span>
        </button>
      </div>,
      20
    );

    if (!this.attrs.token) {
      items.add(
        'password',
        <div className="Form-group">
          <input
            className="FormControl"
            name="password"
            type="password"
            autocomplete="new-password"
            placeholder={passwordLabel}
            aria-label={passwordLabel}
            bidi={this.password}
            disabled={this.loading}
          />
        </div>,
        10
      );
    }

    items.add(
      'submit',
      <div className="Form-group">
        <Button className="Button Button--primary Button--block" type="submit" loading={this.loading}>
          {app.translator.trans('core.forum.sign_up.submit_button')}
        </Button>
      </div>,
      -10
    );
    items.add(
      'hint',
      <div className="Form-group hint">
        <span>{app.translator.trans('dl-response.forum.login.hint_text')}</span>
      </div>,
      -20
    );

    return items;
  }

  sendCode() {
    if (!CommentUtil.checkEmail(this.email())) {
      // this.loading = false;
      CommentUtil.onWarn(app, app.translator.trans('dl-alert.forum.email_rule'));
      return;
    }
    this.isSending(true);
    this.secondsLeft(60);
    let lastRender = 0;
    const timer = setInterval(() => {
      const currentLeft = this.secondsLeft() - 1;
      this.secondsLeft(currentLeft);
      if (Date.now() - lastRender > 500) {
        m.redraw.sync();
        lastRender = Date.now();
      }
      if (currentLeft <= 0) {
        clearInterval(timer);
        this.isSending(false);
        m.redraw();
      }
    }, 1000);
    CommentUtil.onSuccess(app, app.translator.trans('dl-response.forum.warn.send_code_success'));
    app.request({
      url: app.forum.attribute('baseUrl') + '/sendCode',
      method: 'POST',
      params: {
        email: this.email(),
      },
    });
  }

  footer() {
    return [
      <p className="SignUpModal-logIn">{app.translator.trans('core.forum.sign_up.log_in_text', { a: <a onclick={this.logIn.bind(this)} /> })}</p>,
    ];
  }

  /**
   * Open the log in modal, prefilling it with an email/username/password if
   * the user has entered one.
   */
  logIn() {
    const attrs = {
      identification: this.email() || this.username(),
    };

    app.modal.show(LogInModal, attrs);
  }

  onready() {
    if (this.attrs.username && !this.attrs.email) {
      this.$('[name=email]').select();
    } else {
      this.$('[name=username]').select();
    }
  }

  onsubmit(e: SubmitEvent) {
    e.preventDefault();

    this.loading = true;

    const body = this.submitData();
    if (!CommentUtil.checkUsername(body.username)) {
      this.loading = false;
      CommentUtil.onWarn(app, app.translator.trans('dl-alert.forum.username_rule'));
      return;
    }
    if (!CommentUtil.checkEmail(body.email)) {
      this.loading = false;
      CommentUtil.onWarn(app, app.translator.trans('dl-alert.forum.email_rule'));
      return;
    }
    if (!CommentUtil.checkPwd(body.password)) {
      this.loading = false;
      CommentUtil.onWarn(app, app.translator.trans('dl-alert.forum.pwd_rule'));
      return;
    }
    if (!CommentUtil.checkCode(body.code)) {
      this.loading = false;
      CommentUtil.onWarn(app, app.translator.trans('dl_sendCode.forum.code_rule'));
      return;
    }

    app
      .request({
        url: app.forum.attribute('baseUrl') + '/register',
        method: 'POST',
        body,
        errorHandler: this.onerror.bind(this),
      })
      .then((res) => {
        window.location.reload();
        this.loaded.bind(this);
      });
  }

  onerror(error: RequestError) {
    this.loading = false;
    CommentUtil.onWarn(app, error.responseText);
  }

  /**
   * Get the data that should be submitted in the sign-up request.
   */
  submitData(): SignupBody {
    const authData = this.attrs.token ? { token: this.attrs.token } : { password: this.password() };

    const data = {
      username: this.username(),
      email: this.email(),
      code: this.code(),
      fromFlorum: true,
      ...authData,
    };

    return data;
  }
}
